import { useRef, useEffect, useState } from 'react';
import { motion, useInView, useSpring, useTransform } from 'framer-motion';

/**
 * NumberTicker — animated number counter that springs to target value on scroll.
 */
export function NumberTicker({ value, className = '', delay = 0, suffix = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const [hasStarted, setHasStarted] = useState(false);

  const spring = useSpring(0, {
    mass: 1,
    stiffness: 60,
    damping: 20,
  });

  const display = useTransform(spring, (latest) => Math.round(latest));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView && !hasStarted) {
      const timeout = setTimeout(() => {
        spring.set(value);
        setHasStarted(true);
      }, delay * 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, hasStarted, spring, value, delay]);

  useEffect(() => {
    const unsubscribe = display.on('change', (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0, filter: 'blur(4px)' }}
      animate={isInView ? { opacity: 1, filter: 'blur(0px)' } : undefined}
      transition={{ duration: 0.5, delay }}
    >
      {displayValue}{suffix}
    </motion.span>
  );
}
